// Importing React classes and functions from node modules & from components
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { loggedInUser, removeLoggedInUser, removeSelectedId, removeSelectedId2, checkAuth, signOutUser } from "../data/repository";
import '../assets/css/App.css';
import jwtDecode from "jwt-decode";

// Importing the components
import Navigation from './Navigation';
import Header from './Header';
import Home from './Home';
import Footer from './Footer';
import Login from './Login';
import Announcements from './Announcements';
import MessageContext from "../data/MessageContext";
import About from "./About";
import ErrorPage from "./ErrorPage";
import StaffDashboard from "./StaffDashboard";
import AddHomework from "./AddHomework";
import Register from "./Register";
import Resources from "./Resources";
import SelectGroup from './SelectGroup';
import DisplayStudents from './DisplayStudents';
import Syllabus from './Syllabus';
import StudentDashboard from './StudentDashboard';
import Profile from './Profile';
import DisplayStaff from './DisplayStaff';
import Settings from './Settings';
import CreateStaffUser from './CreateStaffUser';
import CreateClass from './CreateClass';
import CreateGroup from './CreateGroup';
import DisplayGroup from './DisplayGroup';
import DisplayClass from './DisplayClass';
import AttendanceStudents from './AttendanceStudents';
import DisplayAttendanceDates from './DisplayAttendanceDates';
import DisplayAttendance from './DisplayAttendance';
import AttendanceStaff from './AttendanceStaff';
import Homework from './Homework';
import DisplayHomework from './DisplayHomework';
import CreateHomework from './CreateHomework';
import DisplayMarkedHomework from './DisplayMarkedHomework';
import StudentResults from './StudentResults';
import ViewAllResults from './ViewAllResults';
import PasswordReset from './PasswordReset';
import PasswordResetForm from './PasswordResetForm';
import MaintenancePage from './Maintenance';

// Functional Component for App
function App() {

  // useState Hooks Defined
  const [decodedUser, setDecodedUser] = useState(null);
  const [message, setMessage] = useState(null);
  const [logoutTimer, setLogoutTimer] = useState(null); // new state variable to hold the logout timer
  const [logoutReason, setLogoutReason] = useState("");


  useEffect(() => {
    // Function to fetch user data from cookie
    const fetchUser = async () => {
      try {
        const response = await checkAuth();
        const { accessToken } = response.data;
        if (accessToken) {
          const decodedUser = jwtDecode(accessToken);
          setDecodedUser(decodedUser); // Decode and store user data

          // Set a logout timer based on token expiration
          const expiresIn = decodedUser.exp * 1000 - Date.now(); // Convert to ms
          if (expiresIn > 0) {
            const timer = setTimeout(logoutUser, expiresIn);
            setLogoutTimer(timer);
          }
        }
      } catch (error) {
        console.error("User validation failed");
        removeLoggedInUser();
        removeSelectedId();
        removeSelectedId2();
      }
    };

    fetchUser();

    // Clear timeout on component unmount or state change
    return () => {
      if (logoutTimer) clearTimeout(logoutTimer);
    };

  }, [logoutTimer]);


  // Set message to null automatically after a period of time
  // useEffect(() => {
  //   if (message !== null) {
  //     const id = setTimeout(() => setMessage(null), 7000);
  //     return () => clearTimeout(id); // Cleanup timeout
  //   }
  // }, [message]);

  useEffect(() => {
    if (message !== null) {
      const id = setTimeout(() => setMessage(null), 7000);
      return () => clearTimeout(id); // Cleanup timeout
    }

    // Only show session expired message if the logout is due to token expiration
    if (logoutReason === "sessionExpired") {
      sessionStorage.setItem("inactiveMessage", "Your session has expired. Please login again to continue!");
      window.location.href = '/Sign-in';
    }
  }, [message, logoutReason]);


  // Const Function for storing in state Variables and for sending to child elements
  const loginUser = (user) => {
    const userDecoded = jwtDecode(user);
    setDecodedUser(userDecoded);
    loggedInUser(userDecoded.name);
    resetLogoutTimer(user.exp);
  };

  // Reset the logout timer on login
  const resetLogoutTimer = (expirationTime) => {
    if (logoutTimer) {
      clearTimeout(logoutTimer);
    }
    const expiresIn = expirationTime * 1000 - Date.now();
    if (expiresIn > 0) {
      const timer = setTimeout(() => setLogoutReason("sessionExpired"), expiresIn);
      setLogoutTimer(timer);
    }
  };

  // Const Function for removing state for user and sending it to child elements
  const logoutUser = async () => {
    await signOutUser();
    setDecodedUser(null);
    removeLoggedInUser();
    removeSelectedId();
    removeSelectedId2();
  };


  // Returns below elements to from the function App.
  return (
    <div>
      {/* Router is used for routing to different pages */}
      {/* Message Contect Provider, useContext Hook Used, Message is being passed down to all child components */}
      <MessageContext.Provider value={{ message, setMessage }}>
        {/* Props being passed to each component and Router is used for Navigating to pages */}
        <Router>

          <Header />
          <Navigation user={decodedUser} loginUser={loginUser} logoutUser={logoutUser} />
          <Routes>

            {decodedUser !== null &&
              <>
                <Route path="/" element={<Navigate to="/Dashboard" replace />} />
                <Route path="/Home" element={<Navigate to="/Dashboard" replace />} />
                <Route path="/Resources" element={<Resources user={decodedUser} loginUser={loginUser} logoutUser={logoutUser} />} />
                <Route path="/Profile" element={<Profile user={decodedUser} loginUser={loginUser} logoutUser={logoutUser} />} />
                <Route path="/Syllabus" element={<Syllabus user={decodedUser} />} />
                <>
                  {(decodedUser.group === "Male Teacher" || decodedUser.group === "Female Teacher" || decodedUser.group === "Admin" || decodedUser.group === "Principal" || decodedUser.group === "Vice Principal" || decodedUser.group === "Admin Staff") &&
                    <>
                      <Route path="/Dashboard" element={<StaffDashboard user={decodedUser} loginUser={loginUser} logoutUser={logoutUser} />} />
                      <Route path="/Announcements" element={<Announcements user={decodedUser} loginUser={loginUser} logoutUser={logoutUser} />} />
                      <Route path="/AddHomework" element={<AddHomework user={decodedUser} />} />
                      <Route path="/SelectGroupHomework" element={<SelectGroup user={decodedUser} selectGroup={"homework"} />} />
                      <Route path="/SelectGroupMarkHomework" element={<SelectGroup user={decodedUser} selectGroup={"markhomework"} />} />
                      <Route path="/SelectGroupStudent" element={<SelectGroup user={decodedUser} selectGroup={"student"} />} />
                      <Route path="/SelectGroupAttendance" element={<SelectGroup user={decodedUser} selectGroup={"attendance"} />} />
                      <Route path="/HomeworkGroup/:groupNumber" element={<DisplayStudents user={decodedUser} group={"homework"} />} />
                      <Route path="/StudentGroup/:groupNumber" element={<DisplayStudents user={decodedUser} group={"student"} />} />
                      <Route path="/Attendance/:groupNumber" element={<AttendanceStudents user={decodedUser} />} />
                      <Route path="/AttendanceStaff" element={<AttendanceStaff user={decodedUser} />} />
                      <Route path="/Homework/:groupNumber/:className" element={<Homework user={decodedUser} />} />
                      <Route path="/SelectGroupResults" element={<SelectGroup user={decodedUser} selectGroup={"result"} />} />
                      <Route path="/Results/:groupNumber" element={<ViewAllResults user={decodedUser} />} />
                    </>
                  }

                  {(decodedUser.group === "Admin" || decodedUser.group === "Principal" || decodedUser.group === "Vice Principal" || decodedUser.group === "Admin Staff") &&
                    <>
                      <Route path="/Register" element={<Register />} />
                      <Route path="/Staff" element={<DisplayStaff user={decodedUser} loginUser={loginUser} logoutUser={logoutUser} />} />
                      <Route path="/Settings" element={<Settings user={decodedUser} loginUser={loginUser} logoutUser={logoutUser} />} />
                      <Route path="/CreateStaffUser" element={<CreateStaffUser user={decodedUser} loginUser={loginUser} logoutUser={logoutUser} />} />
                      <Route path="/CreateGroup" element={<CreateGroup user={decodedUser} loginUser={loginUser} logoutUser={logoutUser} />} />
                      <Route path="/ViewGroup" element={<DisplayGroup user={decodedUser} loginUser={loginUser} logoutUser={logoutUser} />} />
                      <Route path="/CreateClass" element={<CreateClass user={decodedUser} loginUser={loginUser} logoutUser={logoutUser} />} />
                      <Route path="/ViewClass" element={<DisplayClass user={decodedUser} loginUser={loginUser} logoutUser={logoutUser} />} />
                      <Route path="/ViewAttendanceDates" element={<DisplayAttendanceDates user={decodedUser} loginUser={loginUser} logoutUser={logoutUser} />} />
                      <Route path="/ViewAttendance/:date" element={<DisplayAttendance user={decodedUser} loginUser={loginUser} logoutUser={logoutUser} />} />
                      <Route path="/DisplayHomework" element={<DisplayHomework user={decodedUser} loginUser={loginUser} logoutUser={logoutUser} />} />
                      <Route path="/CreateHomework" element={<CreateHomework user={decodedUser} loginUser={loginUser} logoutUser={logoutUser} />} />
                      <Route path="/DisplayResults" element={<DisplayMarkedHomework user={decodedUser} loginUser={loginUser} logoutUser={logoutUser} />} />
                    </>
                  }

                  {(decodedUser.group !== "Male Teacher" && decodedUser.group !== "Female Teacher" && decodedUser.group !== "Admin" && decodedUser.group !== "Principal" && decodedUser.group !== "Vice Principal" && decodedUser.group !== "Admin Staff") &&
                    <>
                      <Route path="/Dashboard" element={<StudentDashboard user={decodedUser} loginUser={loginUser} logoutUser={logoutUser} />} />
                      <Route path="/StudentResults" element={<StudentResults user={decodedUser} />} />
                    </>
                  }

                </>
              </>
            }

            {(decodedUser === null || decodedUser.group === "Admin") && (
              <>
                <Route path="/" element={<Home user={decodedUser} />} />
                <Route path="/Home" element={<Home user={decodedUser} />} />
                <Route path="/Sign-in" element={<Login loginUser={loginUser} logoutUser={logoutUser} />} />
                <Route path="/Password-Reset" element={<PasswordReset loginUser={loginUser} logoutUser={logoutUser} />} />
                <Route path="/Register" element={<Register />} />
                <Route path="/About" element={<About loginUser={loginUser} />} />
                <Route path="/Reset-Password" element={<PasswordResetForm />} />
              </>
            )}

            {decodedUser === null &&
              <Route path="/Dashboard" element={<Navigate to="/Home" replace />} />
            }

            <Route path="/Maintenance" element={<MaintenancePage />} />

            <Route path="*" element={<ErrorPage />} />
          </Routes>
          <Footer />
        </Router>
      </MessageContext.Provider>
    </div>
  );
}

// Export the App Function
export default App;
